import React from 'react';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { Box, Paper } from '@mui/material';

import { AIInsertPlugin } from './plugins/AIInsertPlugin';
import { SwatchInjectionPlugin } from './plugins/SwatchInjectionPlugin';
import { KeyboardScrollIntoViewPlugin } from './plugins/KeyboardScrollIntoViewPlugin';
import { WRITER_LAB_TRANSFORMERS } from './lexicalConfig';
import { useDebouncedDocumentSave } from '../hooks/useDebouncedDocumentSave';

interface Props {
  /** In-memory autosave sink for manual edits (see useDebouncedDocumentSave). */
  onContentChange?: (serializedState: string) => void;
  placeholder?: string;
}

/**
 * The Writer's Lab editing surface. Must be rendered inside the WriterLab
 * shell's <LexicalComposer> (hoisted there so the rail's operation panels share
 * the same editor and can dispatch AI_INSERT). Ships the 6 v3.0 plugins:
 * RichText, History, List, MarkdownShortcut, AIInsert, SwatchInjection (stub),
 * + OnChange for autosave.
 */
export const LexicalDocCanvas: React.FC<Props> = ({ onContentChange, placeholder }) => {
  const handleChange = useDebouncedDocumentSave(onContentChange);

  return (
    <Paper
      elevation={0}
      sx={{ flex: 1, p: 3, overflow: 'auto', bgcolor: 'background.paper', minHeight: 0 }}
      aria-label="Document canvas"
    >
      <Box sx={{ position: 'relative', minHeight: '60vh', maxWidth: 820, mx: 'auto' }}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className="writer-lab-editor"
              style={{ outline: 'none', minHeight: '60vh', fontSize: 16, lineHeight: 1.6 }}
            />
          }
          placeholder={
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                color: 'text.disabled',
                pointerEvents: 'none',
              }}
            >
              {placeholder ?? 'Start writing, or click Generate to begin from a prompt…'}
            </Box>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
      </Box>
      <HistoryPlugin />
      <ListPlugin />
      <MarkdownShortcutPlugin transformers={WRITER_LAB_TRANSFORMERS} />
      <AIInsertPlugin />
      <SwatchInjectionPlugin />
      <KeyboardScrollIntoViewPlugin />
      <OnChangePlugin onChange={handleChange} />
    </Paper>
  );
};
